import React, { useState } from 'react';
import axios from 'axios';
import { Alert, Breadcrumbs, Card, Toggle } from '@opendate-app/setlist';
import CrmPageLayout from '../../shared/components/CrmPageLayout';
import crmLabels from '../../shared/utils/crmLabels';

const featuresFor = (labels) => [
  {
    key: 'soft_credits_enabled',
    label: 'Soft credits',
    description: `Credit a gift to people or ${labels.organization.plural.toLowerCase()} who influenced it. When off, soft credits are hidden on contacts and ${labels.organization.plural.toLowerCase()}; existing ones are kept.`,
  },
  {
    key: 'deals_enabled',
    label: 'Deals',
    description: 'Track sponsorships, asks and other opportunities through a pipeline. When off, Deals disappear from the CRM; existing deals are kept.',
  },
  {
    key: 'grants_enabled',
    label: 'Grants',
    description: 'Track grant applications, deadlines, awards and reporting. When off, Grants disappear from the CRM; existing grants are kept.',
  },
  {
    key: 'volunteers_enabled',
    label: 'Volunteers',
    description: 'Show the Volunteers tab on contacts. When off, the tab is hidden.',
  },
  {
    key: 'pledge_failure_emails_enabled',
    label: 'Pledge payment failure emails',
    description: "Email donors when an automatic pledge payment doesn't go through. Staff are always told.",
  },
];

function errorMessageFrom(error) {
  const errors = error?.response?.data?.errors;
  return Array.isArray(errors) && errors.length > 0 ? errors.join(', ') : 'Could not save. Try again.';
}

export default function CrmDonorFeaturesPage({ team, csrfToken, features: initialFeatures = {} }) {
  const [features, setFeatures] = useState(initialFeatures);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const save = async (key, value) => {
    const previous = features;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    setFeatures({ ...features, [key]: value });
    setSaving(key);
    setError(null);
    try {
      const { data } = await axios.patch(`/teams/${team.id}/crm_donor_features.json`, { team: { [key]: value } });
      setFeatures(data.features);
    } catch (err) {
      setFeatures(previous);
      setError(errorMessageFrom(err));
    } finally {
      setSaving(null);
    }
  };

  return (
    <CrmPageLayout
      team={team}
      activeSidebarKey="settings"
      mainTopMargin={32}
      breadcrumb={<Breadcrumbs items={[{ label: 'Settings', href: `/teams/${team.id}/crm-settings` }, { label: 'Donor Features' }]} />}
      title="Donor Features"
      subtitle="Turn off the parts of the CRM your team doesn't use. Turning one back on restores everything."
    >
      <Card padding="md">
        {error && <div className="mb-3"><Alert color="error" title={error} /></div>}
        {featuresFor(crmLabels(team)).map((feature) => (
          <div key={feature.key} className="py-3 border-bottom">
            <Toggle
              id={`donor-feature-${feature.key}`}
              label={feature.label}
              description={feature.description}
              checked={features[feature.key] !== false}
              disabled={saving === feature.key}
              onChange={(e) => save(feature.key, e.target.checked)}
            />
          </div>
        ))}
      </Card>
    </CrmPageLayout>
  );
}
